import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { z } from 'zod';
import Button from '~/components/buttons/NewButton';
import CountryController from '~/components/controllers/CountryController';
import FiscalRegimeController from '~/components/controllers/FiscalRegimeController';
import TextController from '~/components/controllers/TextController/TextController';
import { VStack } from '~/components/layout/Stack';
import ModalActions from '~/components/modals/Modal/ModalActions';
import Text from '~/components/typographies/Text';
import { useAnalytics } from '~/hooks/useAnalytics';
import { ModalProps } from '~/modals/Modal';
import { AnalyticsEvents } from '~/services/analytics';
import { FiscalRegime } from '~/types/company';
import { countryIsoCodes } from '~/types/country';
import { isItalianTaxNumber, isItalianVatNumber } from '~/utils/validation';
import styles from './CreateCompanyForm.module.scss';

const useCompanyDataSchema = () => {
  const { t } = useTranslation('validations');
  return useMemo(
    () =>
      z.object({
        name: z.string().min(1),
        country: z.enum(countryIsoCodes),
        vatNumber: z
          .string()
          .min(1)
          .length(11, t('exact_string', { count: 11 }))
          .refine(isItalianVatNumber, t('invalid_vat_number')),
        taxNumber: z
          .string()
          .min(1)
          .superRefine((v, ctx) => {
            if (isItalianVatNumber(v) || isItalianTaxNumber(v)) {
              return;
            }

            if (new RegExp(/\d+/).test(v)) {
              return ctx.addIssue({
                message: t('exact_string', { count: 11 }),
                code: z.ZodIssueCode.custom,
              });
            }

            return ctx.addIssue({
              message: t('exact_string', { count: 16 }),
              code: z.ZodIssueCode.custom,
            });
          }),
        fiscalRegime: z.nativeEnum(FiscalRegime),
        address: z.string().min(1),
        city: z.string().min(1),
        postalCode: z.string().min(1),
      }),
    [t],
  );
};

export type CompanyData = z.infer<ReturnType<typeof useCompanyDataSchema>>;

interface CreateCompanyFormProps extends ModalProps {
  initialValues?: Partial<CompanyData>;
  vatNumberDisabled?: boolean;
  onSubmit: (data: CompanyData) => void;
  isLoading?: boolean;
}
const CreateCompanyForm: React.FC<CreateCompanyFormProps> = ({
  initialValues,
  vatNumberDisabled,
  onSubmit,
  closeModal,
  isLoading,
}) => {
  const { t } = useTranslation('auth', { keyPrefix: 'add_company.steps.create_company' });
  const { track } = useAnalytics();

  const companyDataSchema = useCompanyDataSchema();

  const form = useForm<CompanyData>({
    defaultValues: {
      name: '',
      country: 'IT',
      vatNumber: '',
      taxNumber: '',
      fiscalRegime: FiscalRegime.RF01,
      address: '',
      city: '',
      postalCode: '',
      ...initialValues,
    },
    resolver: zodResolver(companyDataSchema),
  });

  return (
    <form noValidate>
      <VStack gap={5}>
        <Text variant="body2" weight="regular">
          <Trans t={t} i18nKey="description" />
        </Text>
        <div className={styles.form}>
          <TextController
            className={styles.fullWidth}
            control={form.control}
            name="name"
            label={t('form.name.label')}
            placeholder={t('form.name.placeholder')}
            required
          />

          <div className={styles.formRow}>
            <CountryController
              control={form.control}
              name="country"
              label={t('form.country.label')}
              required
              className={styles.fullWidth}
            />
            <TextController
              control={form.control}
              name="vatNumber"
              label={t('form.vat_number.label')}
              placeholder={t('form.vat_number.placeholder')}
              required
              className={styles.fullWidth}
              disabled={vatNumberDisabled}
            />
          </div>

          <div className={styles.formRow}>
            <TextController
              className={styles.fullWidth}
              control={form.control}
              name="taxNumber"
              label={t('form.tax_number.label')}
              required
              disabled
            />
            <FiscalRegimeController
              className={styles.fullWidth}
              control={form.control}
              name="fiscalRegime"
              label={t('form.fiscal_regime.label')}
              required
            />
          </div>

          <TextController
            className={styles.fullWidth}
            control={form.control}
            name="address"
            label={t('form.address.label')}
            placeholder={t('form.address.placeholder')}
            required
          />

          <div className={styles.formRow}>
            <TextController
              className={styles.fullWidth}
              control={form.control}
              name="city"
              label={t('form.city.label')}
              placeholder={t('form.city.placeholder')}
              required
            />
            <TextController
              className={styles.smallInput}
              control={form.control}
              name="postalCode"
              label={t('form.postal_code.label')}
              placeholder={t('form.postal_code.placeholder')}
              required
            />
          </div>
        </div>

        <ModalActions className={styles.modalAction}>
          <Button
            onClick={() => {
              closeModal();
            }}
            variant="outlined"
          >
            {t('actions.cancel')}
          </Button>
          <Button
            onClick={() =>
              form.handleSubmit((v) => {
                track(AnalyticsEvents.SECOND_COMPANIES_CREATION_STARTED, {
                  step: 'company_manually_created',
                  metadata: { tax_code: v.taxNumber },
                });
                return onSubmit(v);
              })()
            }
            loading={isLoading}
          >
            {t('actions.next')}
          </Button>
        </ModalActions>
      </VStack>
    </form>
  );
};

export default CreateCompanyForm;
